package ui.app.assign.detail.kindergarten.editor.page

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyStaggeredGridScope
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import apps.App
import apps.App.Kindergarten.AudienceType
import compose.assets.Svg
import compose.assets.svg.Format_text
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.app.assign.detail.kindergarten.editor.AppKindergartenEditorScope
import ui.app.assign.detail.kindergarten.editor.NextButton
import ui.app.assign.detail.kindergarten.editor.icon
import ui.app.assign.detail.kindergarten.editor.label
import ui.app.assign.detail.wheel.editor.widgets.AddIconAction
import ui.widget.ColorField
import ui.widget.EditorCardWrapper
import ui.widget.LazyImage
import ui.widget.MaskTypePicker

@Composable
fun AppKindergartenEditorScope.KindergartenPage00General(nnvl: AppKindergartenEditorScope.Wrapper) {
    LazyVerticalStaggeredGrid(
        columns = StaggeredGridCells.FixedSize(512.dp),
        modifier = Modifier.fillMaxSize(),
        verticalItemSpacing = 16.dp,
        horizontalArrangement = Arrangement.SpaceAround,
        contentPadding = PaddingValues(
            start = 16.dp,
            end = 16.dp,
            top = 16.dp,
            bottom = 32.dp
        )
    ) {
        Item {
            Title(nnvl, Modifier.fillMaxWidth())
            Icon(nnvl, Modifier.fillMaxWidth())
        }
        Item {
            AudienceType(nnvl, Modifier.fillMaxWidth())
            ThemeType(nnvl, Modifier.fillMaxWidth())
        }
        Item {
            Colors(nnvl, Modifier.fillMaxWidth())
            AppBackground(nnvl, Modifier.fillMaxWidth())
        }
        NextButton(this@KindergartenPage00General)
    }
}


@Composable
fun AppKindergartenEditorScope.Title(nnvl: AppKindergartenEditorScope.Wrapper, modifier: Modifier) {
    TextField(
        value = nnvl.name.text,
        isError = nnvl.name.err,
        onValueChange = { nnvl.name.set(it) },
        modifier = modifier.clip(CardDefaults.shape),
        leadingIcon = { Icon(Svg.Format_text, "") },
        label = { Text(Res.string.layer_bottom_editor__label_name.rememberString()) },
        shape = CardDefaults.shape
    )
}


@Composable
fun AppKindergartenEditorScope.Icon(
    nnvl: AppKindergartenEditorScope.Wrapper,
    modifier: Modifier
) {
    EditorCardWrapper(
        icon = 103638300,
        title = Res.string.layer_bottom__kindergarten_icon_title,
        tooltip = Res.string.layer_bottom__kindergarten_icon_title,
        modifier = modifier
    ) {
        Box(Modifier.fillMaxWidth()) {
            LazyImage(
                id = nnvl.icon.state,
                contentScale = ContentScale.Fit,
                alphaBackground = true,
                modifier = Modifier.size(256.dp)
                    .align(Alignment.Center)
            )
            AddIconAction(
                companyId = args.companyId,
                entryId = args.appId,
                canEdit = true,
                image = nnvl.icon,
                small = false,
                orientation = ResourceOrientation.UNDEFINED,
                extraKeyImage = "kindergarten:default-icon:${args.appId}",
                canBeIcon = true,
                modifier = Modifier.align(Alignment.BottomEnd)
            )
        }
    }
}


@Composable
fun AppKindergartenEditorScope.AudienceType(
    nnvl: AppKindergartenEditorScope.Wrapper,
    modifier: Modifier
) {
    EditorCardWrapper(
        icon = 22486300,
        title = Res.string.layer_bottom__kindergarten_audience_title,
        tooltip = Res.string.layer_bottom__kindergarten_audience_tooltip,
        modifier = modifier
    ) {
        MaskTypePicker(
            items = AudienceType.all,
            field = nnvl.audienceType,
            icon = { it.icon() },
            label = { it.label() }
        )
    }
}


@Composable
fun AppKindergartenEditorScope.ThemeType(
    nnvl: AppKindergartenEditorScope.Wrapper,
    modifier: Modifier
) {
    EditorCardWrapper(
        icon = 32297300,
        title = Res.string.layer_bottom__kindergarten_theme_title,
        tooltip = Res.string.layer_bottom__kindergarten_theme_tooltip,
        modifier = modifier
    ) {
        MaskTypePicker(
            items = App.Kindergarten.ThemeType.all.filter {
                it in arrayOf(
                    App.Kindergarten.ThemeType.AnimalsLocal,
                    App.Kindergarten.ThemeType.AnimalsExotic,
                    App.Kindergarten.ThemeType.Family,
                    App.Kindergarten.ThemeType.Transportation,
                    App.Kindergarten.ThemeType.Food
                )
            },
            field = nnvl.themeType,
            icon = { it.icon() },
            label = { it.label() }
        )
    }
}

@Composable
fun AppKindergartenEditorScope.Colors(
    nnvl: AppKindergartenEditorScope.Wrapper,
    modifier: Modifier
) {
    EditorCardWrapper(
        icon = 54590300,
        title = Res.string.layer_bottom__kindergarten_colors_title,
        tooltip = Res.string.layer_bottom__kindergarten_colors_tooltip,
        modifier = modifier
    ) {

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            ColorField(
                field = nnvl.colorSurface,
                label = Res.string.layer_bottom__kindergarten_colors_surface,
                colors = TextFieldDefaults.colors(
                    focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                    unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                    disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                ),
                shape = CardDefaults.shape,
                modifier = Modifier.fillMaxWidth().clip(CardDefaults.shape)
            )

            ColorField(
                field = nnvl.colorOnSurface,
                label = Res.string.layer_bottom__kindergarten_colors_on_surface,
                colors = TextFieldDefaults.colors(
                    focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                    unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                    disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                ),
                shape = CardDefaults.shape,
                modifier = Modifier.fillMaxWidth().clip(CardDefaults.shape)
            )
        }
    }
}

@Composable
fun AppKindergartenEditorScope.AppBackground(
    nnvl: AppKindergartenEditorScope.Wrapper,
    modifier: Modifier
) {
    EditorCardWrapper(
        icon = 103638300,
        title = Res.string.layer_bottom__kindergarten_background_title,
        tooltip = Res.string.layer_bottom__kindergarten_background_tooltip,
        bodyPadding = 0.dp,
        modifier = modifier
    ) {
        Box(Modifier.fillMaxWidth()) {
            LazyImage(
                id = nnvl.backgroundImg.state,
                contentScale = ContentScale.Fit,
                alphaBackground = false,
                modifier = Modifier.fillMaxWidth()
                    .aspectRatio(16F / 9F)
            )
            AddIconAction(
                companyId = args.companyId,
                entryId = args.appId,
                canEdit = true,
                image = nnvl.backgroundImg,
                small = false,
                orientation = ResourceOrientation.HORIZONTAL,
                extraKeyImage = "kindergarten:default-bg:${args.appId}",
                canBeIcon = false,
                modifier = Modifier.align(Alignment.BottomEnd)
            )
        }
    }
}

private fun LazyStaggeredGridScope.Item(body: @Composable ColumnScope.() -> Unit) {
    item {
        Card(
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainer
            )
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                content = body
            )
        }
    }
}